import { authClient } from '@/app/clients/auth.client';
import { DEFAULT_COUNTRY_CODE } from '@/helper/phone';
import type { PlaceParsedAddress } from '@/models/place-address.model';
import React, {
  createContext,
  useState,
  ReactNode,
  useContext,
  useEffect,
} from 'react';

// Define the shape of the user
interface User {
  name: string | undefined;
  phone: string;
  phoneCountry?: string;
  admin: boolean;
  placeAddress?: PlaceParsedAddress;
}

// Define the shape of the context
interface SessionContextType {
  user: User | undefined | null;
  setUser: React.Dispatch<React.SetStateAction<User | undefined | null>>;
  sessionInitialized: boolean;
}

// Create the context
const SessionContext = createContext<SessionContextType | undefined>(undefined);

// Create the provider component
export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | undefined | null>(undefined);

  useEffect(() => {
    if (user) return;
    authClient
      .current()
      .then((response) => {
        if (response.data.isAuthenticated) {
          setUser({
            name: response.data.isAdmin ? 'Admin' : response.data.name,
            phone: response.data.phone,
            phoneCountry:
              response.data.phoneCountry || DEFAULT_COUNTRY_CODE,
            admin: response.data.isAdmin,
            placeAddress: response.data.placeAddress,
          });
        } else {
          setUser(null);
        }
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
        setUser(null);
      });
  }, [user]);

  return (
    <SessionContext.Provider
      value={{ user, setUser, sessionInitialized: user !== undefined }}
    >
      {children}
    </SessionContext.Provider>
  );
};

// Create a custom hook to use the session context
export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
